import { h } from '@atomify/jsx';
import { useUrlParams } from '@bpd-library/utilities';
import { LoginScreenInterface } from '@source/components/molecules/login-screen/login-screen';
import { fetchBuyerStatus } from '@utilities/api-calls/buyer-status';
import { elements } from '@utilities/dom';
import Environment from '@utilities/environment';
import { GTMPush } from '@utilities/gtm';
import { sha256 } from '@utilities/sha-256';
import md5 from 'md5';

import { updateWishlist as updateStoreWishlist, userStore } from '../store';
import {
    MY_HOME_IFRAME_LOGIN_MESSAGE_NAME,
    MY_HOME_IFRAME_MESSAGE_EVENT_NAME,
    WEBSITE_IFRAME_LOGIN_MESSAGE_NAME,
} from './constants';
import {
    addToWishlistApiCall,
    clearData,
    fetchUserData,
    fillUserStore,
    getMyAccountCookie,
    getSessionStorageData,
    removeFromWishlistApiCall,
    setMyAccountCookie,
} from './services';

let loginScreen: null | LoginScreenInterface = null;

export const checkLoggedInUser = async () => {
    const sessionStorageData = getSessionStorageData();
    const cookieToken = getMyAccountCookie();

    window.addEventListener('message', handleLoginData);

    const { get: getParam, clearParam } = useUrlParams();
    if (!!getParam('user-redirect')) {
        clearParam('user-redirect');
        login();
        return;
    }

    if (sessionStorageData) {
        // Session storage will be set when user store is updated via sessionStorageStore
        return;
    }

    if (cookieToken) {
        const userData = await fetchUserData(cookieToken);

        if (userData) {
            fillUserStore(userData);
        }
    }
};

export const handleGTM = async () => {
    const { areas, projects, propertyTypes, info } = userStore.getState();

    const toIdList = (arr?: { Id: string }[]) =>
        arr?.length ? arr.map(({ Id }) => Id) : undefined;

    const trackingEvent = {
        event: 'initUser',
        userId: info?.id,
        loggedIn: !!info,
        email: info?.email && md5(info.email),
        email_sha_256: info?.email && (await sha256(info.email)),
        favorite_areas: toIdList(areas),
        favorite_projects: toIdList(projects),
        favorite_houses: toIdList(propertyTypes),
    };

    GTMPush(trackingEvent);
};

const handleLoginData = async (e: MessageEvent) => {
    const { event, loginToken } = e.data;
    if (event !== WEBSITE_IFRAME_LOGIN_MESSAGE_NAME || !loginToken) return;

    loginScreen?.closeAndRemove();

    const userData = await fetchUserData(loginToken);
    if (userData) fillUserStore(userData);

    setMyAccountCookie(loginToken);
};

export const getUserInfo = () => {
    return userStore.getState().info;
};

export const getUserAreas = () => {
    return userStore.getState().areas;
};

export const getUserWishlist = () => {
    return userStore.getState().wishlist;
};

export const isLoggedIn = () => {
    return userStore.getState().loggedIn;
};

export const login = () => {
    loginScreen = <bpd-login-screen />;

    if (loginScreen) elements.body.append(loginScreen);
};

export const logout = () => {
    clearData();

    if (!window.__ENVIRONMENT_DETAILS__.logoutUrl) {
        if (!Environment.isProduction)
            console.error('logoutUrl is missing on window.__ENVIRONMENT_DETAILS__ object');
        return;
    }

    const logoutIframe = document.createElement('iframe');
    logoutIframe.title = 'Logout iframe';
    logoutIframe.src = window.__ENVIRONMENT_DETAILS__.logoutUrl;
    logoutIframe.classList.add('u-hidden');

    elements.body.append(logoutIframe);

    if (!!document.querySelector('[js-hook-account-frame]')) {
        setTimeout(() => window.location.reload(), 2000);
    }
};

export const wishlistContains = (constructionNumberId: string) => {
    const wishlist = getUserWishlist() || [];
    const index = wishlist.indexOf(constructionNumberId);

    return index > -1;
};

export const addToWishlist = async (constructionNumberId: string) => {
    // 1. Update store and sessionstorage
    const wishlist = getUserWishlist() || [];

    if (Environment.isLocal) {
        // If the environment is local, just add it without logging in
        updateStoreWishlist([constructionNumberId, ...wishlist]);
    } else {
        // 2. If not logged in, add after user is logged in
        if (!isLoggedIn()) {
            login();

            // 3. Add constructionNumberId when user is logged in
            userStore.subscribe(
                function addWhenLoggedIn({ loggedIn }) {
                    if (loggedIn && !wishlistContains(constructionNumberId)) {
                        addToWishlist(constructionNumberId);
                        userStore.unsubscribe(addWhenLoggedIn);
                    }
                },
                ['loggedIn'],
            );

            return;
        }

        // 4. When user is logged in, immediatly update store for visual feedback
        updateStoreWishlist([constructionNumberId, ...wishlist]);

        // 5. Api call to add to wishlist
        try {
            await addToWishlistApiCall(constructionNumberId);
        } catch (error) {
            // 6. If the api call fails, remove it from the store again
            updateStoreWishlist(wishlist);

            console.log('error addToWishlist ', error);
            return;
        }
    }
};

export const removeFromWishlist = async (constructionNumberId: string) => {
    // 1. Update store
    const wishlist = getUserWishlist() || [];
    const originalWishlist = [...wishlist];
    const index = wishlist.indexOf(constructionNumberId);

    if (index > -1) {
        wishlist.splice(index, 1);
        updateStoreWishlist(wishlist);
    }

    if (!Environment.isLocal) {
        if (!isLoggedIn()) {
            if (!Environment.isProduction)
                console.error('User must be logged in to remove favourite');
            return;
        }

        // 2. Api call to remove from wishlist
        try {
            await removeFromWishlistApiCall(constructionNumberId);
        } catch (error) {
            // If the api call fails, add it back to the wishlist
            updateStoreWishlist(originalWishlist);
            console.log('error removeFromWishlist ', error);
            return;
        }
    }
};

export const wishlistLength = () => {
    const wishlist = getUserWishlist() || [];
    return wishlist.length;
};

export const getUserBuyerStatus = async (constructionNumberId: string) => {
    const { id } = getUserInfo()!;
    return await fetchBuyerStatus(id, constructionNumberId);
};

type MehIframeEvent = {
    event: string;
} & Record<string, any>;

const handleMehIframeEvents = (data: MehIframeEvent) => {
    if (data.event === MY_HOME_IFRAME_LOGIN_MESSAGE_NAME) {
        if (!isLoggedIn()) login();
    } else {
        GTMPush(data);
    }
};

document.addEventListener(MY_HOME_IFRAME_MESSAGE_EVENT_NAME, (e: CustomEvent) =>
    handleMehIframeEvents(e.detail),
);
